import { login, getUserInfo } from '@/api/user'
import { jsonInBlacklist } from '@/api/jwt'
import router from '@/router/index'
import { ElLoading, ElMessage } from 'element-plus'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouterStore } from './router'
import { useCookies } from '@vueuse/integrations/useCookies'
import { useStorage } from '@vueuse/core'
import { getS3Config, getPlatformTime } from '@/api/basic'
import { useAppStore } from '@/pinia/modules/app'
import { emitter } from '@/utils/bus.js'

export const useUserStore = defineStore('user', () => {
  const appStore = useAppStore()
  const loadingInstance = ref(null)


  const countryCode = ref(import.meta.env.VITE_COUNTRY || 'Mexico')
  const currencyCode = ref('MXN')
  const s3BaseUrl = ref('') // S3 基础 URL
  const timeZone = ref(import.meta.env.VITE_TIMEZONE || 'America/Mexico_City')

  // 平台时区相关
  const platformTimezone = ref('') // 平台时区,例如: America/Mexico_City
  const platformTime = ref('') // 平台当前时间

  const userInfo = ref({
    uuid: '',
    nickName: '',
    headerImg: '',
    authority: {}
  })
  const token = useStorage('token', '')
  const xToken = useCookies('x-token')
  const currentToken = computed(() => token.value || xToken.value || '')

  const setUserInfo = (val) => {
    userInfo.value = val
    if (val.originSetting) {
      Object.keys(appStore.config).forEach((key) => {
        if (val.originSetting[key] !== undefined) {
          appStore.config[key] = val.originSetting[key]
        }
      })
    }
  }

  const fetchS3Config = async () => {
    try {
      const res = await getS3Config()
      if (res.code === 0 && res.data?.s3_base_url) {
        s3BaseUrl.value = res.data.s3_base_url
        // 设置时区
        if (res.data.time_zone) {
          timeZone.value = res.data.time_zone
          console.log(`✅ 当前平台时区: ${timeZone.value}`)
        }
      } else {
        console.error('❌ 警告: 后端未返回 s3_base_url 配置!')
        ElMessage.error('平台s3地址未配置,请联系管理员')
      }
    } catch (error) {
      console.error('获取S3配置失败:', error)
    }
  }

  // 获取平台时间
  const fetchPlatformTime = async () => {
    try {
      const res = await getPlatformTime()
      if (res.code === 0) {
        platformTimezone.value = res.data.timezone || 'America/Mexico_City'
        platformTime.value = res.data.current_time || ''
        return res.data
      }
    } catch (error) {
      console.error('获取平台时间失败:', error)
    }
    return null
  }


  const setToken = (val) => {
    token.value = val
    xToken.value = val
  }

  const NeedInit = async () => {
    await ClearStorage()
    await router.push({ name: 'Init', replace: true })
  }

  const ResetUserInfo = (value = {}) => {
    userInfo.value = {
      ...userInfo.value,
      ...value
    }
  }
  /* 获取用户信息*/
  const GetUserInfo = async () => {
    fetchS3Config()
    const res = await getUserInfo()

    if (res.code === 0) {
      setUserInfo(res.data.userInfo)
    }
    return res
  }
  /* 登录*/
  const LoginIn = async (loginInfo) => {
    try {
      loadingInstance.value = ElLoading.service({
        fullscreen: true,
        text: '登录中，请稍候...'
      })

      const res = await login(loginInfo)

      if (res.code !== 0) {
        if (res?.data?.bind === false || res?.data?.token === "") {
          return {
            showBind: true,
            flag: false,
            res: res
          }
        }

        return {
          showBind: res?.data?.bind === false,  // 判断是否需要显示绑定弹窗
          flag: false,
          res: res  // 必须返回res才能获取qrCode数据
        }
      }



      fetchS3Config()
      // 登陆成功，设置用户信息和权限相关信息
      setUserInfo(res.data.user)
      setToken(res.data.token)

      // 初始化路由信息
      const routerStore = useRouterStore()
      await routerStore.SetAsyncRouter()
      const asyncRouters = routerStore.asyncRouters


      // 注册到路由表里
      asyncRouters.forEach((asyncRouter) => {
        router.addRoute(asyncRouter)
      })

      if (router.currentRoute.value.query.redirect) {
        await router.replace(router.currentRoute.value.query.redirect)
        return {
          flag: true
        }
      }

      if (!router.hasRoute(userInfo.value.authority.defaultRouter)) {
        ElMessage.error('请联系管理员进行授权')
      } else {
        await router.replace({ name: userInfo.value.authority.defaultRouter })
      }

      const isWindows = /windows/i.test(navigator.userAgent)
      window.localStorage.setItem('osType', isWindows ? 'WIN' : 'MAC')

      // 全部操作均结束，关闭loading并返回
      return {
        flag: true
      }
    } catch (error) {
      console.error('LoginIn error:', error)
      return {
        flag: false
      }
    } finally {
      loadingInstance.value?.close()
    }
  }
  /* 登出*/
  const LoginOut = async () => {
    const res = await jsonInBlacklist()

    // 登出失败
    if (res.code !== 0) {
      return
    }

    await ClearStorage()

    // 把路由定向到登录页，无需等待直接reload
    router.push({ name: 'Login', replace: true })
    window.location.reload()
  }
  /* 清理数据 */
  const ClearStorage = async () => {
    token.value = ''
    xToken.value = ''
    sessionStorage.clear()
    localStorage.removeItem('originSetting')
  }

  return {
    countryCode,
    currencyCode,
    s3BaseUrl,
    timeZone,
    platformTimezone,
    platformTime,
    fetchPlatformTime,
    userInfo,
    token: currentToken,
    NeedInit,
    ResetUserInfo,
    GetUserInfo,
    LoginIn,
    LoginOut,
    setToken,
    loadingInstance,
    ClearStorage
  }
})
